import { useMemo } from 'react';
import { useOrgUsersQuery } from './hooks';
import type { OrganizationUser } from '@/types/user-management.types';

/**
 * Returns a Map<userId, OrganizationUser> for O(1) lookups.
 * Wraps useOrgUsersQuery so the underlying 5-min React Query cache is shared.
 */
export function useUserMap(orgId: string | undefined) {
  const { data, isLoading } = useOrgUsersQuery(orgId);

  const users = useMemo(() => data?.users ?? [], [data]);

  const userMap = useMemo(() => {
    const map = new Map<string, OrganizationUser>();
    for (const user of users) {
      map.set(user.id, user);
    }
    return map;
  }, [users]);

  return { userMap, users, isLoading };
}

/** Pure helper — returns display_name or fallback. */
export function getUserDisplayName(
  userMap: Map<string, OrganizationUser>,
  userId: string | undefined | null,
  fallback = ''
): string {
  if (!userId) return fallback;
  return userMap.get(userId)?.display_name ?? fallback;
}
